// Shot curves as plain series for the curve component of the design system: a stored shot (protocol v1 channels), the
// running shot (live points on a 1-s grid), the bars of the last shots. No drawing here.
import { decodeChannel, type CurveChannel } from '@brewcore/protocol';
import type { ShotItem } from '@brewcore/app-contract';
import type { LivePoint } from '../live-shot.js';
import { metaOf, shotTime } from './format.js';

export interface CurveData {
  /** ms between two samples */
  dt: number;
  weight: (number | null)[] | null;
  flow: (number | null)[] | null;
  temp: (number | null)[] | null;
  /** index of the first sample after the pump stopped, -1 unknown */
  stop: number;
}

/** A shot's curve (protocol v1 channels) on one grid; null without channels. */
export function curveData(curve: Record<string, unknown> | undefined): CurveData | null {
  const channels = (curve?.channels ?? []) as CurveChannel[];
  if (!Array.isArray(channels) || channels.length === 0) return null;
  const get = (key: string) => {
    const c = channels.find((ch) => ch.key === key && Array.isArray(ch.data));
    return c ? decodeChannel(c) : null;
  };
  return {
    dt: channels[0]?.dt ?? 1000,
    weight: get('weight'),
    flow: get('flow'),
    temp: get('temp'),
    stop: typeof curve?.stop === 'number' ? curve.stop : -1,
  };
}

/** The running shot: one sample a second; temperature and weight carried over gaps, flow only where measured. */
export function liveCurveData(points: readonly LivePoint[]): CurveData | null {
  if (points.length < 2) return null;
  const n = Math.round(points.at(-1)?.s ?? 0) + 1;
  const w: (number | null)[] = Array<number | null>(n).fill(null);
  const f: (number | null)[] = Array<number | null>(n).fill(null);
  const t: (number | null)[] = Array<number | null>(n).fill(null);
  for (const p of points) {
    const k = Math.min(n - 1, Math.max(0, Math.round(p.s)));
    if (p.weight !== null) w[k] = p.weight;
    if (p.flow !== null) f[k] = p.flow;
    if (p.temp !== null) t[k] = p.temp;
  }
  for (let k = 1; k < n; k++) {
    if (t[k] === null) t[k] = t[k - 1] ?? null;
    if (w[k] === null && w[k - 1] !== null) w[k] = w[k - 1] ?? null;
  }
  return {
    dt: 1000,
    weight: w.some((v) => v !== null) ? w : null,
    flow: f.some((v) => v !== null) ? f : null,
    temp: t,
    stop: -1,
  };
}

/** Length of a curve in seconds. */
export const curveSeconds = (d: CurveData | null | undefined): number =>
  d ? Math.max(d.weight?.length ?? 0, d.flow?.length ?? 0, d.temp?.length ?? 0) * (d.dt / 1000) : 0;

/** Slots of the bar chart: always as wide as for 12 shots, the newest on the right (a few shots stay narrow bars). */
export const SHOT_BAR_SLOTS = 12;

/** Bars of the last shots, oldest first (at most 12): time without PI and weight. */
export function shotBars(items: readonly ShotItem[]): { id: string; time: number; weight: number | null }[] {
  return items
    .slice(0, 12)
    .map((s) => {
      const m = metaOf(s);
      return { id: s.id, time: shotTime(m).main, weight: typeof m.weight === 'number' ? m.weight : null };
    })
    .reverse();
}
